import React, { useEffect, useMemo } from 'react';
import {
  Paper,
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Button,
  Chip,
  alpha,
  Tooltip,
  Divider,
  useTheme,
  Card,
  CardHeader,
  Stack,
  Badge,
  LinearProgress,
  Grid
} from '@mui/material';
import { 
  MoreVert, 
  Repeat, 
  Stars, 
  Error, 
  Person, 
  ArrowForward, 
  AttachMoney, 
  CalendarToday, 
  MonetizationOn,
  Payments,
  TrendingUp,
  PeopleAlt,
  BarChart,
  PersonOutline,
  Email
} from '@mui/icons-material';
import { formatTimeAgo, formatCurrency } from '../utils/formatters';

interface DonorInfo {
  id: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  lastDonationAmount?: number;
  lastDonationDate?: string;
  lastPaymentDate?: string;
  totalDonations?: number;
  donationCount?: number;
  createdAt?: string;
}

interface RecentDonorsProps {
  recentDonors: DonorInfo[];
  onViewAll: () => void;
}

// Define different avatar colors based on donation amount for visual hierarchy
const getAvatarColor = (amount: number = 0) => {
  if (amount >= 5000) return { bg: '#f44336', color: '#fff' }; // High value donors - red
  if (amount >= 1000) return { bg: '#ff9800', color: '#fff' }; // Medium-high - orange
  if (amount >= 500) return { bg: '#2196f3', color: '#fff' }; // Medium - blue
  if (amount >= 100) return { bg: '#4caf50', color: '#fff' }; // Medium-low - green
  return { bg: '#9e9e9e', color: '#fff' }; // Standard - gray
};

// Generate initials from name for avatar
const getInitials = (donor: DonorInfo): string => {
  // If we have firstName and lastName, use those
  if (donor.firstName && donor.lastName) {
    return (donor.firstName.charAt(0) + donor.lastName.charAt(0)).toUpperCase();
  }
  
  // If we have a combined name field
  if (donor.name) {
    const parts = donor.name.split(' ').filter(part => part.length > 0);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }
  
  // If we have email but no name
  if (donor.email) {
    return donor.email.charAt(0).toUpperCase();
  }
  
  return '?';
};

// Get donor's full name
const getDonorName = (donor: DonorInfo): string => {
  if (!donor) {
    return 'Unknown Donor';
  }

  if (donor.firstName && donor.lastName) {
    return `${donor.firstName} ${donor.lastName}`;
  }
  
  if (donor.name) {
    return donor.name;
  }
  
  if (donor.email) {
    return donor.email;
  }
  
  return donor.id ? `Donor-${donor.id.substring(0, 8)}` : 'Unknown Donor';
};

const RecentDonors: React.FC<RecentDonorsProps> = ({ recentDonors, onViewAll }) => {
  const theme = useTheme();
  const renderCount = React.useRef(0);
  
  useEffect(() => {
    renderCount.current++;
  });
  
  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    
    try {
      // Try standard date parsing
      let date = new Date(dateString);
      
      // If date is invalid, try parsing M/D/YYYY format
      if (isNaN(date.getTime())) {
        const parts = dateString.split('/');
        if (parts.length === 3) {
          const month = parseInt(parts[0]) - 1; // months are 0-indexed in JS
          const day = parseInt(parts[1]);
          const year = parseInt(parts[2]);
          date = new Date(year, month, day);
        }
      }
      
      if (isNaN(date.getTime())) {
        console.error(`Invalid date string: ${dateString}`);
        return 'Invalid date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Error formatting date';
    }
  };
  
  const formatAmount = (amount?: number): string => {
    if (amount === undefined) return 'N/A';
    
    try {
      return formatCurrency(amount);
    } catch (error) {
      console.error('Error formatting amount:', error);
      return 'Error';
    }
  };
  
  const hasValidDonors = Array.isArray(recentDonors) && recentDonors.length > 0;

  // Calculate the total donations from all donors in the list
  const totalDonationsAmount = useMemo(() => {
    if (!hasValidDonors) return 0;
    return recentDonors.reduce((sum, donor) => sum + (donor.totalDonations || donor.lastDonationAmount || 0), 0);
  }, [recentDonors, hasValidDonors]);

  // Calculate the total number of donations
  const totalDonationsCount = useMemo(() => {
    if (!hasValidDonors) return 0;
    return recentDonors.reduce((sum, donor) => sum + (donor.donationCount || 1), 0);
  }, [recentDonors, hasValidDonors]);

  if (!hasValidDonors) {
    return (
      <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ height: 5, bgcolor: theme.palette.primary.main }}></Box>
        <CardHeader
          title="Recent Donor Activity"
          sx={{
            '& .MuiCardHeader-title': {
              fontSize: '1.1rem',
              fontWeight: 600,
            },
            pb: 1
          }}
        />
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No recent donations to display
          </Typography>
        </Box>
      </Card>
    );
  }
  
  const donorItems = useMemo(() => {
    return recentDonors.slice(0, 5).map((donor, index) => {
      if (!donor) return null;
      
      const donorName = getDonorName(donor);
      const donationCount = donor.donationCount || 1;
      const totalDonations = donor.totalDonations || donor.lastDonationAmount || 0;
      const lastAmount = donor.lastDonationAmount || (totalDonations / donationCount);
      const avatarColors = getAvatarColor(totalDonations);
      const isRecurring = donationCount > 1;
      const initials = getInitials(donor);
      const lastDonationDate = donor.lastDonationDate || donor.lastPaymentDate || donor.createdAt;
      
      // Calculate a simple donation rating for the visual indicator
      // Higher value = larger donation relative to this list
      const maxAmount = Math.max(...recentDonors.map(d => d.totalDonations || d.lastDonationAmount || 0));
      const donationRating = maxAmount > 0 ? (totalDonations / maxAmount) * 100 : 0;
      
      return (
        <React.Fragment key={donor.id || index}>
          {index > 0 && <Divider component="li" />}
          <ListItem 
            sx={{ 
              py: 2.5, 
              px: 3,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.light, 0.05),
              }
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    isRecurring ? (
                      <Tooltip title={`Recurring donor (${donationCount} donations)`}>
                        <Repeat 
                          fontSize="small" 
                          sx={{ 
                            bgcolor: theme.palette.primary.main,
                            color: 'white',
                            borderRadius: '50%',
                            padding: '3px',
                            width: 16, 
                            height: 16,
                            boxShadow: theme.shadows[2]
                          }} 
                        />
                      </Tooltip>
                    ) : null
                  }
                >
                  <Avatar 
                    sx={{ 
                      bgcolor: avatarColors.bg, 
                      color: avatarColors.color,
                      width: 45,
                      height: 45,
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      boxShadow: theme.shadows[2]
                    }}
                  >
                    {initials}
                  </Avatar>
                </Badge>
              </Grid>
              
              <Grid item xs>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
                    <Box>
                      <Tooltip title={donor.email || ''}>
                        <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ maxWidth: 180, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {donorName}
                          {donor.email && <Email fontSize="inherit" color="disabled" sx={{ ml: 0.5, opacity: 0.7 }} />}
                        </Typography>
                      </Tooltip>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarToday fontSize="inherit" /> {formatDate(lastDonationDate)}
                      </Typography>
                    </Box>
                    <Stack direction="column" alignItems="flex-end" spacing={0.5}>
                      {lastAmount > 0 && (
                        <Tooltip title="Last donation amount">
                          <Chip
                            label={formatAmount(lastAmount)}
                            size="small"
                            color="primary"
                            sx={{ 
                              fontWeight: 'bold', 
                              minWidth: 80,
                              boxShadow: theme.shadows[1]
                            }}
                          />
                        </Tooltip>
                      )}
                      
                      <Tooltip title="Total donations to date">
                        <Chip
                          label={formatAmount(totalDonations)}
                          size="small"
                          color="secondary"
                          variant={totalDonations === lastAmount ? "outlined" : "filled"}
                          sx={{ 
                            fontWeight: 'bold', 
                            minWidth: 80,
                            boxShadow: theme.shadows[1]
                          }}
                          icon={<MonetizationOn fontSize="small" />}
                        />
                      </Tooltip>
                    </Stack>
                  </Box>
                  
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Chip
                        icon={<Payments fontSize="small" />}
                        label={`${donationCount} donation${donationCount !== 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ height: 24 }}
                      />
                      <Typography variant="caption" color="primary" fontWeight="medium">
                        {donationRating.toFixed(0)}% of max
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={donationRating} 
                      sx={{ 
                        height: 6, 
                        borderRadius: 3,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '.MuiLinearProgress-bar': {
                          bgcolor: avatarColors.bg
                        }
                      }} 
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </ListItem>
        </React.Fragment>
      );
    });
  }, [recentDonors, theme]);
  
  return (
    <Card sx={{ 
      mb: 3, 
      boxShadow: 3, 
      borderRadius: 2,
      overflow: 'hidden',
      transition: 'all 0.3s ease-in-out',
      '&:hover': {
        boxShadow: 5,
        transform: 'translateY(-2px)'
      }
    }}>
      <Box sx={{ height: 5, bgcolor: theme.palette.primary.main }}></Box>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AttachMoney sx={{ mr: 1, color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Recent Donor Activity
            </Typography>
          </Box>
        }
        subheader={
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <MonetizationOn fontSize="inherit" color="secondary" /> Total: {formatAmount(totalDonationsAmount)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Payments fontSize="inherit" color="primary" /> Donations: {totalDonationsCount}
            </Typography>
          </Box>
        }
        action={
          <Button
            color="primary"
            endIcon={<ArrowForward />}
            onClick={onViewAll}
            size="small"
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            View All
          </Button>
        }
        sx={{
          pb: 1,
          '& .MuiCardHeader-content': {
            overflow: 'hidden'
          }
        }}
      />
      <List disablePadding>
        {donorItems}
        
        {recentDonors.length > 5 && (
          <>
            <Divider />
            <Box sx={{ p: 2, textAlign: 'center', bgcolor: alpha(theme.palette.primary.light, 0.05) }}>
              <Button
                color="primary"
                endIcon={<ArrowForward />}
                onClick={onViewAll}
                size="small"
                sx={{ fontWeight: 'medium' }}
              >
                View All {recentDonors.length} Donors
              </Button>
            </Box>
          </>
        )}
      </List>
    </Card>
  );
};

export default React.memo(RecentDonors); 